import React, { FunctionComponent, useMemo, useState } from "react";
import { Boolean, DataTableDef, SelectionMode } from "@plex/react-components";
import { getDataTypeFromSchemaInstance, getListSchemaRow, getSchema } from "../NodeTypes/DataSchemas";
import { DocumentSchemaType, IDocumentObjectSchema } from "../FlowDocument/FlowDocumentModel";
import { DataType, isPrimitiveLike } from "../NodeTypes/TypeDefinitions";
import { IViewController, useViewController } from "../ViewContext";

export interface IUserDefinedSchemaPropertiesGridProps {
  schemaId?: string;
  selectedPropertyName?: string;
  onSelectionChanged?: (propertyName: string | undefined) => void;
}

interface IUserDefinedSchemaPropertyRow {
  name: string;
  type: string;
  system?: string;
  objectName?: string;
  required: boolean;
  isList: boolean;
}

export const UserDefinedSchemaPropertiesGrid: FunctionComponent<IUserDefinedSchemaPropertiesGridProps> = (props) => {
  const { schemaId, onSelectionChanged } = props;
  const [selected, setSelected] = useState<IUserDefinedSchemaPropertyRow[]>([]);
  const [gridDataState, setGridDataState] = useState<IUserDefinedSchemaPropertyRow[]>([]);
  const { userDefinedSchemaDetailsDialogState }: IViewController = useViewController();

  useMemo(() => {
    const schema = schemaId ? (getSchema(schemaId) as unknown as IDocumentObjectSchema) : undefined;
    setGridDataState(
      schema?.properties
        ? Object.entries(schema.properties).map(([name, property]) => {
            const propertySchema = getSchema(property.schema);
            const propertySchemaDataType = getDataTypeFromSchemaInstance(propertySchema);
            const propertySchemaRow = getListSchemaRow(propertySchema, false);
            return {
              name,
              type: propertySchemaRow.dataType,
              objectName: !isPrimitiveLike(propertySchemaDataType as DataType) ? propertySchemaRow.name : undefined,
              system: propertySchemaRow.system,
              isList: propertySchema.schemaType === DocumentSchemaType.list,
              required: property.required
            };
          })
        : []
    );
  }, [schemaId, schemaId ? JSON.stringify(getSchema(schemaId)) : undefined]);

  return (
    <DataTableDef<IUserDefinedSchemaPropertyRow>
      data={gridDataState}
      keySelector={(row: IUserDefinedSchemaPropertyRow) => row.name}
      selectionMode={SelectionMode.single}
      className="user-defined-schema-properties-grid"
      emptyText="No properties defined."
      selected={selected}
      onSelectionChanged={(selectedRows: IUserDefinedSchemaPropertyRow[]) => {
        setSelected(selectedRows);
        onSelectionChanged?.(selectedRows.length > 0 ? selectedRows[0]!.name : undefined);
      }}
    >
      <DataTableDef.Column<IUserDefinedSchemaPropertyRow> id="1" title="Name" sortable>
        {(row: IUserDefinedSchemaPropertyRow) => row.name}
      </DataTableDef.Column>
      <DataTableDef.Column<IUserDefinedSchemaPropertyRow> id="2" title="Type" sortable>
        {(row: IUserDefinedSchemaPropertyRow) => row.type}
      </DataTableDef.Column>
      <DataTableDef.Column<IUserDefinedSchemaPropertyRow> id="3" title="System" sortable>
        {(row: IUserDefinedSchemaPropertyRow) => row.system}
      </DataTableDef.Column>
      <DataTableDef.Column<IUserDefinedSchemaPropertyRow> id="4" title="Object Name" sortable>
        {(row: IUserDefinedSchemaPropertyRow) => row.objectName}
      </DataTableDef.Column>
      <DataTableDef.Column<IUserDefinedSchemaPropertyRow> id="5" title="List">
        {(row: IUserDefinedSchemaPropertyRow) => <Boolean value={row.isList} />}
      </DataTableDef.Column>
      {!userDefinedSchemaDetailsDialogState.loopVariable && (
        <DataTableDef.Column<IUserDefinedSchemaPropertyRow> id="6" title="Required">
          {(row: IUserDefinedSchemaPropertyRow) => <Boolean value={row.required} />}
        </DataTableDef.Column>
      )}
    </DataTableDef>
  );
};
